/**
 * CG-4 — useGuardianMode hook (coach-guardian phase, v1.1).
 *
 * The nav-level guardian detection (design N1/N2, founder-ratified
 * 2026-07-03):
 *
 *   - `isGuardian`   — the signed-in user ACTIVELY guards ≥1 athlete
 *                      (an adult-side `guardian` relationship with
 *                      status 'active'). Drives the account-menu
 *                      "Switch to guardian view" entry (dual accounts).
 *   - `guardianOnly` — guards someone OR declared guardian intent at
 *                      the onboarding fork (CG-7: `_account_role ===
 *                      'guardian'`, set before any guardianship can be
 *                      active), AND no completed athlete onboarding
 *                      (`_onboarding_completed` absent from athlete
 *                      context). These accounts get the reduced
 *                      Home / Approvals / Settings nav at every tier.
 *
 * Data source is the same `GET /api/relationships` endpoint behind
 * useRelationships, but resolved through a module-level per-user cache
 * (the useAdminGate pattern): this hook mounts inside nav chrome
 * (BottomTab / SideNav / TopBar) on every route, and an uncached fetch
 * per mount would hammer the endpoint for the 99% of users who guard
 * nobody. Staleness note: becoming a guardian mid-session (accepting an
 * invite) shows up on the next full page load — same trade-off the
 * admin gate makes.
 */

import { useEffect, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { useAthleteContext } from './useAthleteContext';
import { useAuthFetch } from './useAuthFetch';

export interface GuardianModeState {
  /** True until auth + the relationships check resolve. */
  loading: boolean;
  /** True when the user actively guards at least one athlete. */
  isGuardian: boolean;
  /** True for guardian-only accounts (guards someone, never onboarded
   *  as an athlete) — these get the reduced 3-item nav. */
  guardianOnly: boolean;
}

// Per-user-id caches (useAdminGate pattern). `cache` holds the resolved
// guardian check; `inflight` dedupes a concurrent first fetch.
const cache = new Map<string, boolean>();
const inflight = new Map<string, Promise<boolean>>();

/** Test-only — clear the module-level guardian-check cache. */
export function __resetGuardianModeCache(): void {
  cache.clear();
  inflight.clear();
}

export function useGuardianMode(): GuardianModeState {
  const { loading: authLoading, session, user } = useAuthContext();
  const { raw, loading: contextLoading } = useAthleteContext();
  const fetchAuthed = useAuthFetch();
  const [check, setCheck] = useState<{ loading: boolean; isGuardian: boolean }>({
    loading: true,
    isGuardian: false,
  });

  // Depend on stable primitives, not the session/user object identities
  // (some AuthContext consumers/mocks recreate those per render), and
  // no-op state writes when nothing changed — this hook mounts in nav
  // chrome, so an identity-churn re-render loop would hang the app.
  const uid = user?.id ?? null;
  const hasSession = !!session;

  useEffect(() => {
    const settle = (isGuardian: boolean) =>
      setCheck((s) =>
        !s.loading && s.isGuardian === isGuardian ? s : { loading: false, isGuardian },
      );

    if (authLoading) return;
    if (!hasSession || !uid) {
      settle(false);
      return;
    }

    let cancelled = false;

    const cached = cache.get(uid);
    if (cached !== undefined) {
      settle(cached);
      return;
    }

    const fetchCheck =
      inflight.get(uid) ??
      (async (): Promise<boolean> => {
        const res = await fetchAuthed('/api/relationships');
        if (!res.ok) throw new Error(`relationships check failed (${res.status})`);
        const json = (await res.json()) as {
          enabled?: boolean;
          relationships?: Array<{
            role?: string;
            status?: string;
            mySide?: string;
          }>;
        };
        const guards =
          json.enabled === true &&
          Array.isArray(json.relationships) &&
          json.relationships.some(
            (r) => r.mySide === 'adult' && r.role === 'guardian' && r.status === 'active',
          );
        cache.set(uid, guards);
        return guards;
      })();
    inflight.set(uid, fetchCheck);

    fetchCheck
      .then((guards) => {
        if (!cancelled) setCheck({ loading: false, isGuardian: guards });
      })
      .catch(() => {
        // Fail quiet — nav simply renders the athlete default.
        if (!cancelled) setCheck({ loading: false, isGuardian: false });
      })
      .finally(() => {
        inflight.delete(uid);
      });

    return () => {
      cancelled = true;
    };
  }, [authLoading, hasSession, uid, fetchAuthed]);

  const athleteOnboarded = raw._onboarding_completed === true;
  // CG-7 — the fork choice counts as guardian mode BEFORE any
  // guardianship activates, so a fresh guardian account gets the
  // reduced nav (and never the athlete onboarding push) while their
  // invite is still pending.
  const guardianIntent = raw._account_role === 'guardian';
  const loading = authLoading || check.loading || contextLoading;

  return {
    loading,
    isGuardian: check.isGuardian,
    guardianOnly: !loading && (check.isGuardian || guardianIntent) && !athleteOnboarded,
  };
}
